import {Injectable} from '@angular/core';
import {BaseService} from '../../core/base.service';
import {Observable} from 'rxjs/Observable';
import {
  Post,
  PostActivityLineChartDataItem,
  PostActivityPieChartDataItem,
  PostChannel,
  RelatedPost,
  SameInfluencerTopPost
} from '../../../models/post-overview';
import {apiEndpoints, CommonConstant, mentionStatus, platformIconPath, platformIconPathById} from '../../app.constants';
import {Kpi} from '../../../models/common';
import {BackendHttpService} from '../../core/backend-http.service';
import { ErrorObservable } from 'rxjs/observable/ErrorObservable';
import {TopExposure} from '../../../models/influencer';
import {InfluencerService} from '../influencer/influencer.service';
import {Router} from '@angular/router';
import {TranslateService} from '@ngx-translate/core';

@Injectable()
export class InfluencerPostService extends BackendHttpService<any> {
  constructor(protected router: Router,
              protected translate: TranslateService,
              private influencerService: InfluencerService) {

    super(router, translate);
  }
  public getPostContent(urlHash: string, influencerId: number): Observable<Post> {
    const apiUrl = `${apiEndpoints.getPostDetails.replace(':urlHash', urlHash)}`;

    return this.get(apiUrl, {influencerId})
      .map((res: any) => {
        if (res && res.data) {
          const normalizedPost = Object.assign({}, res.data, {
            url: res.data.fullUrl,
            video: res.data.videoUrl,
            image: res.data.imageUrl,
            isStory: res.data.story,
            completed: res.data.completed,
            isVideo: this.influencerService.isVideoPost(res.data),
            channelUrl: `https://${res.data.relativePath}`
          });
          return normalizedPost;
        } else {
          return null;
        }
      });
  }

  public getPostMentions(fullUrl, relativePath): Observable<string[]> {
    const apiUrl = `${apiEndpoints.getCurrentPostMentions}`;
    const apiParams = {
      blog_post_url: fullUrl,
      relativepath: relativePath,
    };

    return this.post(apiUrl, apiParams)
      .map((res: any) => {
        return res.data;
      });
  }

  public getPostActivityLineChartData(urlHash, relativePath, postPublishedDate): Observable<any> {
    const apiUrl = `${apiEndpoints.getPostOverviewLineChart}`;
    const apiParams = {
      url_hash: urlHash,
      relativepath: relativePath
    };

    return this.get(apiUrl, apiParams).map((res: any) => res.postOverviewLineChart);
  }

  public getPostDailyKpis(urlHash, influencerId, period, startDate, endDate): Observable<any> {
    const apiUrl = `${apiEndpoints.getPostDailyKpis.replace(':urlHash', urlHash)}`;
    return this.get(apiUrl, {influencerId, period, periodFrom: startDate, periodTo: endDate ? endDate : ''})
               .map((res: any) => res.data);
  }

  public getPostKpi(fullUrl, relativePath): Observable<Kpi> {
    const apiUrl = `${apiEndpoints.getBlogPostKpi}`;
    const apiParams = {
      blog_post_url: fullUrl,
      relativepath: relativePath,
    };

    return this.post(apiUrl, apiParams)
      .map((res: any) => {
        const postKpi = new Kpi();
        postKpi.engagement.value = res.data.engagement;
        postKpi.impact.value = res.data.impact;
        postKpi.impressions.value = res.data.impressions;
        postKpi.reach.value = res.data.reach;
        return postKpi;
      });
  }

  public getPostActivityGaugeChartData(urlHash, relativePath): Observable<Array<PostActivityPieChartDataItem>> {
    const apiUrl = `${apiEndpoints.getBlogpostOverviewPieChart}`;
    const apiParams = {
      url_hash: urlHash,
      relativepath: relativePath,
    };

    return this.get(apiUrl, apiParams).map((res: any) => {
      return res.blogpostOverviewPieChart ? res.blogpostOverviewPieChart.chartData : null;
    });
  }

  public getTopPosts(influencerId, numberOfPosts, performingScore): Observable<Array<Post>> {
    const apiUrl = `${apiEndpoints.getTopPosts.replace(':influencerId', influencerId)}`;
    const apiParams = {
      period: 30,
      limit: numberOfPosts ? numberOfPosts : 6,
      performingScore
    };

    return this.get(apiUrl, apiParams).map((res: any) => {
      return res.data;
    });
  }

  public getRelatedPosts(influencerId): Observable<TopExposure[]> {
    const apiUrl = `${apiEndpoints.getRelatedPosts.replace(':influencerId', influencerId)}`;
    const apiParams = {
      period: 30,
    };

    return this.get(apiUrl, apiParams).map((res: any) => {
      if (!res || !res.data) {
        return null;
      }
      const relatedPosts = new Array<TopExposure>();
      if (res.data.reach) {
        const reachPost = new TopExposure();
        reachPost.date = res.data.reach.publishedDate;
        reachPost.picture = res.data.reach.imageUrl;
        reachPost.title = res.data.reach.title;
        reachPost.value = res.data.reach.reach;
        reachPost.platform = res.data.reach.platform;
        reachPost.influencer_id = res.data.reach.influencerId;
        reachPost.urlHash = res.data.reach.urlHash;
        reachPost.relativepath = res.data.reach.relativePath;
        relatedPosts.push(reachPost);
      }

      if (res.data.engagement) {
        const engagementPost = new TopExposure();
        engagementPost.date = res.data.engagement.publishedDate;
        engagementPost.picture = res.data.engagement.imageUrl;
        engagementPost.title = res.data.engagement.title;
        engagementPost.value = res.data.engagement.engagement;
        engagementPost.platform = res.data.engagement.platform;
        engagementPost.influencer_id = res.data.engagement.influencerId;
        engagementPost.urlHash = res.data.engagement.urlHash;
        engagementPost.relativepath = res.data.engagement.relativePath;
        relatedPosts.push(engagementPost);
      }

      if (res.data.impact) {
        const impactPost = new TopExposure();
        impactPost.date = res.data.impact.publishedDate;
        impactPost.picture = res.data.impact.imageUrl;
        impactPost.title = res.data.impact.title;
        impactPost.value = res.data.impact.impact * 100;
        impactPost.platform = res.data.impact.platform;
        impactPost.influencer_id = res.data.impact.influencerId;
        impactPost.urlHash = res.data.impact.urlHash;
        impactPost.relativepath = res.data.impact.relativePath;
        relatedPosts.push(impactPost);
      }

      return relatedPosts;
    });
  }

  public addNewMention(keyword: string, keywordType: number | 1, languages: number[] | [0], topicId: number, industryId: number) {
    const apiUrl = `${apiEndpoints.addNewMention}`;
    const apiParams = {
      keyword: keyword,
      keywordType: keywordType,
      languages: languages,
      topic_id: topicId,
      vertical_id: industryId,
    };

    return this.post(apiUrl, JSON.stringify(apiParams)).map((res: any) => {
      return res.data;
    });
  }

  public checkPostMentionExist(mention: string) {
    const apiUrl = `${apiEndpoints.checkPostMentionExist}`;
    const apiParams = {
      mention: mention,
    };
    return this.get(apiUrl, apiParams).map((res: any) => {
      return res.data;
    });
  }

  public approveMention(keywordId: number, languages: number[] = [0], topicId: number, industryId: number) {
    const apiUrl = `${apiEndpoints.setApprovedMention}/${keywordId}`;
    const apiParams = {
      topic_id: topicId,
      vertical_id: industryId,
      languages: languages,
    };
    return this.post(apiUrl, JSON.stringify(apiParams)).map((res: any) => {
      return res.data;
    });
  }

  public addNewPostMention(keywordId: number, blogPostUrl: string, blogPostPublishDate: any, postRelativePath: string, blogPostUrlHash: string, influencerId: number) {
    const apiUrl = `${apiEndpoints.addNewBlogPostMentions}`;

    const apiParams = {
      blog_post_url: blogPostUrl,
      keywords: keywordId,
      published_date: blogPostPublishDate,
      relativepath: postRelativePath,
      url_hash: blogPostUrlHash,
      influencerId: influencerId
    };

    return this.post(apiUrl, JSON.stringify(apiParams)).map((res: any) => {
      return res.data;
    });
  }

  public getPlatformIconById(id: number) {
    return platformIconPathById[id] !== undefined ? platformIconPathById[id].path : '';
  }
}



// WEBPACK FOOTER //
// ./src/app/services/influencer-post/influencer-post.service.ts